'use client';

import React from 'react';
import Link from 'next/link';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import WhatsAppFloat from '@/components/WhatsAppFloat';
import { Shield, Clock, Star, Users, Award, CheckCircle } from 'lucide-react';

const values = [
  {
    icon: <Shield size={24} />,
    title: 'Keamanan & Keselamatan',
    desc: 'Armada kami selalu dalam kondisi prima dengan perawatan rutin dan driver berlisensi resmi.',
  },
  {
    icon: <Clock size={24} />,
    title: 'Ketepatan Waktu',
    desc: 'Kami memahami bahwa waktu Anda sangat berharga. Ketepatan waktu adalah komitmen utama kami.',
  },
  {
    icon: <Star size={24} />,
    title: 'Pelayanan Prima',
    desc: 'Kepuasan pelanggan adalah prioritas kami. Setiap perjalanan dirancang untuk memberikan pengalaman terbaik.',
  },
  {
    icon: <Users size={24} />,
    title: 'Tim Profesional',
    desc: 'Driver dan staf kami terlatih secara profesional untuk memberikan pelayanan yang ramah dan terpercaya.',
  },
];

const stats = [
  { value: '5+', label: 'Tahun Berdiri' },
  { value: '50+', label: 'Armada Kendaraan' },
  { value: '999% ', label: 'Pelanggan Puas' },
  { value: '24/7', label: 'Layanan Tersedia' },
];

const achievements = [
  'Armada terawat dengan servis berkala',
  'Driver berpengalaman & berlisensi resmi',
  'Melayani perjalanan dalam & luar kota',
  'Tersedia untuk kebutuhan pribadi & korporat',
  'Sistem booking mudah via WhatsApp & website',
  'Harga transparan tanpa biaya tersembunyi',
];

export default function TentangKamiPage() {
  return (
    <div className="min-h-screen bg-background">
      <Navbar />

      {/* Hero */}
      <section className="relative pt-28 pb-16 bg-primary overflow-hidden">
        <div className="absolute inset-0 bg-hero-pattern" />
        <div className="absolute inset-0 opacity-5">
          <div className="absolute top-10 left-10 w-64 h-64 rounded-full border border-accent/30" />
          <div className="absolute bottom-10 right-10 w-96 h-96 rounded-full border border-accent/20" />
        </div>
        <div className="relative max-w-screen-2xl mx-auto px-4 sm:px-6 lg:px-8 xl:px-10 text-center">
          <div className="inline-flex items-center gap-2 bg-accent/10 border border-accent/20 rounded-full px-4 py-1.5 mb-5">
            <span className="text-accent text-xs font-bold uppercase tracking-widest">Tentang Kami</span>
          </div>
          <h1 className="text-3xl sm:text-4xl lg:text-5xl font-extrabold text-white mb-4 leading-tight">
            NAYVERA INDO TRANSPORT<br />
            <span className="text-gradient-gold">Your Journey, Our Commitment</span>
          </h1>
          <p className="text-white/70 text-base sm:text-lg max-w-2xl mx-auto leading-relaxed">
            Perusahaan transportasi profesional yang berkomitmen memberikan solusi perjalanan terbaik untuk setiap kebutuhan Anda.
          </p>
        </div>
      </section>

      {/* Stats */}
      <section className="py-10 bg-white border-b border-border">
        <div className="max-w-screen-2xl mx-auto px-4 sm:px-6 lg:px-8 xl:px-10">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-6">
            {stats?.map((stat, i) => (
              <div key={`stat-${i}`} className="text-center py-4">
                <div className="text-3xl sm:text-4xl font-extrabold text-gradient-gold mb-1">{stat?.value}</div>
                <div className="text-sm text-muted-foreground font-medium">{stat?.label}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* About Content */}
      <section className="py-16 lg:py-20">
        <div className="max-w-screen-2xl mx-auto px-4 sm:px-6 lg:px-8 xl:px-10">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            {/* Left */}
            <div>
              <div className="section-divider mb-5" />
              <h2 className="text-2xl sm:text-3xl font-extrabold text-foreground mb-5 leading-tight">
                Siapa Kami?
              </h2>
              <div className="space-y-4 text-muted-foreground leading-relaxed">
                <p>
                  <strong className="text-foreground">NAYVERA INDO TRANSPORT</strong> adalah perusahaan rental kendaraan profesional yang melayani kebutuhan transportasi pribadi, korporat, wisata, dan berbagai acara di wilayah Jakarta, Tangerang, dan sekitarnya.
                </p>
                <p>
                  Kami hadir dengan komitmen untuk memberikan layanan transportasi yang aman, nyaman, tepat waktu, dan terpercaya. Dengan armada yang terawat dan driver yang berpengalaman, kami siap menjadi mitra perjalanan terbaik Anda.
                </p>
                <p>
                  [Deskripsi perusahaan lebih lanjut dapat diperbarui melalui dashboard admin — termasuk tahun berdiri, legalitas, dan informasi resmi perusahaan.]
                </p>
              </div>

              <div className="mt-8 grid grid-cols-1 sm:grid-cols-2 gap-3">
                {achievements?.map((item, i) => (
                  <div key={`ach-${i}`} className="flex items-start gap-2.5">
                    <CheckCircle size={16} className="text-accent shrink-0 mt-0.5" />
                    <span className="text-sm text-muted-foreground">{item}</span>
                  </div>
                ))}
              </div>
            </div>

            {/* Right — Company Info Card */}
            <div className="bg-primary rounded-2xl p-8 text-white">
              <div className="flex items-center gap-3 mb-6">
                <Award size={28} className="text-accent" />
                <h3 className="text-xl font-bold">Profil Perusahaan</h3>
              </div>
              <div className="space-y-4">
                {[
                  { label: 'Nama Perusahaan', value: 'NAYVERA INDO TRANSPORT' },
                  { label: 'Bidang Usaha', value: 'Rental Kendaraan & Transportasi' },
                  { label: 'Tahun Berdiri', value: '10 Oktober 2018' },
                  { label: 'Alamat', value: 'Jl. Atang Sanjaya No.Rt 01, RT.002/RW.005, Benda, Kec. Benda, Kota Tangerang, Banten 15125' },
                  { label: 'Wilayah Layanan', value: 'JABODETABEK, LAMPUNG, PALEMBANG, KALIMANTAN, CIREBON, SEMARANG, JOGJA, SURABAYA DAN BALI.' },
                  { label: 'Jam Operasional', value: '24 Jam / 7 Hari' },
                ]?.map((item, i) => (
                  <div key={`info-${i}`} className="flex gap-3 pb-3 border-b border-white/10 last:border-0 last:pb-0">
                    <span className="text-white/50 text-sm w-36 shrink-0">{item?.label}</span>
                    <span className="text-white/90 text-sm font-medium">{item?.value}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Values */}
      <section className="py-16 bg-muted">
        <div className="max-w-screen-2xl mx-auto px-4 sm:px-6 lg:px-8 xl:px-10">
          <div className="text-center mb-12">
            <div className="section-divider mx-auto mb-4" />
            <h2 className="text-2xl sm:text-3xl font-extrabold text-foreground">Nilai-Nilai Kami</h2>
            <p className="text-muted-foreground mt-2 max-w-xl mx-auto">
              Prinsip yang menjadi landasan setiap layanan yang kami berikan.
            </p>
          </div>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {values?.map((val, i) => (
              <div key={`val-${i}`} className="bg-white rounded-xl p-6 border border-border hover:border-accent/40 hover:shadow-md transition-all duration-300">
                <div className="w-11 h-11 rounded-xl bg-primary/8 text-primary flex items-center justify-center mb-4">
                  {val?.icon}
                </div>
                <h3 className="font-bold text-foreground mb-2">{val?.title}</h3>
                <p className="text-sm text-muted-foreground leading-relaxed">{val?.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-14 bg-primary">
        <div className="max-w-screen-2xl mx-auto px-4 sm:px-6 lg:px-8 xl:px-10 text-center">
          <h2 className="text-2xl sm:text-3xl font-extrabold text-white mb-3">
            Siap Memulai Perjalanan?
          </h2>
          <p className="text-white/65 mb-7 max-w-xl mx-auto">
            Percayakan kebutuhan transportasi Anda kepada kami. Booking sekarang dan rasakan perbedaannya.
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <Link href="/booking-page" className="btn-primary px-8 py-3.5 rounded-lg font-bold text-sm">
              BOOKING SEKARANG
            </Link>
            <Link href="/kontak-page" className="btn-outline-gold px-8 py-3.5 rounded-lg font-bold text-sm">
              HUBUNGI KAMI
            </Link>
          </div>
        </div>
      </section>

      <Footer />
      <WhatsAppFloat />
    </div>
  );
}
